import type { KeyboardEventHandler } from 'react';
import React, { useRef, useState } from 'react';
import map from 'lodash/map';
import { Input } from '@zillow/constellation';
import { useClickAway } from 'react-use';
import { getGlobalLogger } from '@zg-rentals/logger-base';

import constants from 'app/shared/constants/ConstantsBundle';
import IconHistory from 'images/icons/history.svg';

// Styles
import * as S from './styles';

// types
import type AutocompleteAddressModelType from 'app/shared/models/AutocompleteAddress';
type AugmentedAutocompleteAddressType = AutocompleteAddressModelType & {
  isRecent?: boolean;
  unit?: string;
};

const logger = getGlobalLogger('components/autocomplete');

const Autocomplete = ({
  items,
  onChange,
  onSelect,
  placeholder = 'Search',
  value = '',
}: {
  items: Array<AugmentedAutocompleteAddressType>;
  onChange: (query: string) => void;
  onSelect: (selection: AugmentedAutocompleteAddressType) => void;
  placeholder: string;
  value: string;
}) => {
  const ref = useRef(null);
  const [isFocused, toggleFocus] = useState(false);
  const [activeIndex, updateActiveIndex] = useState(-1);

  useClickAway(ref, () => toggleFocus(false));

  const handleFocus = (): void => {
    toggleFocus(true);
  };

  const handleChange = (e: React.FormEvent<HTMLInputElement>): void => {
    // need e.persist() so that the debounced change handler has reference to the event
    // https://reactjs.org/docs/events.html#event-pooling
    e.persist();
    // @ts-ignore https://github.com/desktop/desktop/issues/297
    onChange(e.target.value);
  };

  const handleSelect = (item: AugmentedAutocompleteAddressType): void => {
    onSelect(item);
    toggleFocus(false);
  };

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
    const keyCode = e.which;
    toggleFocus(true);

    if (keyCode === constants.UP_ARROW_KEYCODE) {
      const upOneIndex = activeIndex === -1 ? items.length - 1 : activeIndex - 1;
      updateActiveIndex(upOneIndex);
      e.preventDefault();
    } else if (keyCode === constants.DOWN_ARROW_KEYCODE) {
      const downOneIndex = activeIndex === items.length - 1 ? -1 : activeIndex + 1;
      updateActiveIndex(downOneIndex);
      e.preventDefault();
    } else if (keyCode === constants.ESC_KEYCODE) {
      toggleFocus(false);
    } else if (keyCode === constants.TAB_KEYCODE) {
      toggleFocus(false);
    } else if (keyCode === constants.ENTER_KEYCODE) {
      e.preventDefault();
      // if no activeIndex, default to first selection
      const selectedItem = activeIndex === -1 ? items[0] : items[activeIndex];
      if (selectedItem) {
        handleSelect(selectedItem);

        // @ts-ignore https://github.com/desktop/desktop/issues/297
        e.target.blur();
      } else {
        logger?.warn('User did not select an option.');
      }
    }
  };

  const renderDropdown = (): React.ReactNode => {
    if (!isFocused) {
      return null;
    } else if (items.length) {
      return <S.Dropdown>{map(items, (item, idx) => renderDropdownItem({ item, idx }))}</S.Dropdown>;
    } else {
      return null;
    }
  };

  const renderDropdownItem = ({
    idx,
    item,
  }: {
    idx: number;
    item: AugmentedAutocompleteAddressType;
  }): React.ReactNode => {
    return (
      <S.DropdownItem
        isActive={idx === activeIndex}
        key={idx}
        onClick={() => handleSelect(items[idx])}
        onMouseEnter={() => updateActiveIndex(idx)}
        onMouseMove={() => updateActiveIndex(idx)}
      >
        <S.DropdownItemText key={idx}>
          {item.isRecent && <img src={IconHistory} width="16px" height="16px" style={{ marginRight: '12px' }} />}
          {item.name}
        </S.DropdownItemText>
      </S.DropdownItem>
    );
  };

  /**
   * Note about disabling chrome's autocomplete:
   * autocomplete="off | false" prop on <input> should work, however Foundation's
   * Input does not pass `...rest` to the <input>. Luckily, you can disable
   * autocomplete for an entire form in the same way, so a somewhat hacky
   * workaround is to wrap the Input in a form. The 'data-' prefix used to embed
   * custom attributes on HTML elements and fix typescript errors.
   */
  return (
    <S.AutocompleteContainer>
      <form ref={ref} data-autocomplete="off">
        <Input
          onChange={handleChange}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          value={value}
        />

        {renderDropdown()}
      </form>
    </S.AutocompleteContainer>
  );
};

export default Autocomplete;
